import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import { codeRepoAPI, assignmentAPI } from '../services/api';

const CodeRepoDetail = () => {
  const { repoCode } = useParams();
  const navigate = useNavigate();
  const [repo, setRepo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submissions, setSubmissions] = useState({});

  useEffect(() => {
    fetchRepoDetails();
  }, [repoCode]);

  const fetchRepoDetails = async () => {
    try {
      setLoading(true);
      const response = await codeRepoAPI.getRepoByCode(repoCode);
      setRepo(response.data);
      
      // Fetch user submissions for all assignments
      if (response.data.assignments && response.data.assignments.length > 0) {
        await fetchSubmissions(response.data.assignments);
      }
      
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching code repo details');
      console.error('Error fetching repo details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async (assignments) => {
    try {
      // Use getAllMySubmissions instead of getMyAssignments to include course assignments
      const mySubmissionsResponse = await assignmentAPI.getAllMySubmissions();
      const allSubmissions = mySubmissionsResponse.data.submissions || [];
      
      // Create a map of assignment code to submission
      const submissionMap = {};
      allSubmissions.forEach(sub => {
        if (sub.assignment?.assignmentCode) {
          submissionMap[sub.assignment.assignmentCode] = sub;
        }
      });
      
      setSubmissions(submissionMap);
    } catch (err) {
      console.error('Error fetching submissions:', err);
    }
  };

  // Helper function to format time spent
  const formatTimeSpent = (seconds) => {
    if (!seconds || seconds === 0) return null;
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${secs}s`;
    }
  };

  // Helper function to get total steps count
  const getTotalSteps = (assignmentCode) => {
    const submission = submissions[assignmentCode];
    if (!submission || !submission.assignment) return 0;
    
    const assignmentData = submission.assignment;
    if (assignmentData.algorithmByLanguage && Object.keys(assignmentData.algorithmByLanguage).length > 0) {
      const firstLang = Object.keys(assignmentData.algorithmByLanguage)[0];
      return assignmentData.algorithmByLanguage[firstLang]?.steps?.length || 0;
    }
    return assignmentData.steps?.length || 0;
  };

  const calculateProgress = (assignment) => {
    const submission = submissions[assignment.assignmentCode];
    if (!submission) return 0;
    
    const totalSteps = getTotalSteps(assignment.assignmentCode);
    if (totalSteps === 0) return 0;
    
    const completedSteps = submission.stepSubmissions?.filter(ss => ss.isComplete).length || 0;
    // Calculate progress with 80% for steps, 20% for quizzes
    const stepsProgress = (completedSteps / totalSteps) * 80;
    const quizProgress = submission.isCompleted ? 20 : 0;
    return Math.round(stepsProgress + quizProgress);
  };

  const getAssignmentStatus = (assignment) => {
    const submission = submissions[assignment.assignmentCode];
    if (!submission) return { status: 'not-started', label: 'Not Started', color: '#9ca3af' };
    
    if (submission.isCompleted) {
      return { status: 'completed', label: 'Completed', color: '#10b981' };
    }
    
    return { status: 'in-progress', label: 'In Progress', color: '#f59e0b' };
  };

  const calculateOverallProgress = () => {
    if (!repo?.assignments || repo.assignments.length === 0) return 0;
    
    const totalAssignments = repo.assignments.length;
    const completedAssignments = repo.assignments.filter(assignment => {
      const submission = submissions[assignment.assignmentCode];
      return submission?.isCompleted;
    }).length;
    
    return Math.round((completedAssignments / totalAssignments) * 100);
  };

  const handleSolveAssignment = (assignmentId, assignmentCode) => {
    navigate(`/coding-assignment/${assignmentCode}?repoCode=${repo.repoCode}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'beginner': return '#10b981';
      case 'intermediate': return '#f59e0b';
      case 'advanced': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  if (loading) {
    return (
      <div className="container" style={{ backgroundColor: 'white', minHeight: '100vh', borderRadius: '12px' }}>
        {/* Header Card Skeleton */}
        <div className="card">
          <div style={{ marginBottom: '24px' }}>
            {/* Back Button */}
            <Skeleton variant="rectangular" width={180} height={32} sx={{ borderRadius: '6px', mb: 2 }} />
            
            {/* Title, Code and Level Badges */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
              <Skeleton variant="text" width={300} height={32} />
              <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: '6px' }} />
              <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: '6px' }} />
            </div>
            
            {/* Description */}
            <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
            
            {/* Meta Info Badges */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px' }}>
              <Skeleton variant="rectangular" width={120} height={28} sx={{ borderRadius: '6px' }} />
              <Skeleton variant="rectangular" width={200} height={28} sx={{ borderRadius: '6px' }} />
              <Skeleton variant="rectangular" width={140} height={28} sx={{ borderRadius: '6px' }} />
            </div>
            
            {/* Tags */}
            <div style={{ marginTop: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[1, 2, 3].map(i => (
                <Skeleton key={i} variant="rectangular" width={80} height={26} sx={{ borderRadius: '6px' }} />
              ))}
            </div>
          </div>
        </div>

        {/* Overall Progress Card Skeleton */}
        <div className="card" style={{ padding: '20px', marginBottom: '20px', border: '1.5px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Skeleton variant="text" width={180} height={24} />
            <Skeleton variant="text" width={50} height={28} />
          </div>
          <Skeleton variant="rectangular" width="100%" height={12} sx={{ borderRadius: '6px', mb: 1.5 }} />
          <div style={{ display: 'flex', gap: '16px' }}>
            <Skeleton variant="text" width={140} height={18} />
            <Skeleton variant="text" width={120} height={18} />
          </div>
        </div>

        {/* Assignments Section */}
        <div style={{ marginBottom: '20px' }}>
          <Skeleton variant="text" width={200} height={32} sx={{ mb: 2.5 }} />
          
          {/* Assignment Cards */}
          <div style={{ display: 'grid', gap: '15px' }}>
            {[1, 2, 3].map(i => (
              <div key={i} className="card" style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    {/* Number, Title and Badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                      <Skeleton variant="text" width={40} height={28} />
                      <Skeleton variant="text" width={250} height={26} />
                      <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '4px' }} />
                      <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: '4px' }} />
                    </div>
                    
                    {/* Description */}
                    <div style={{ marginLeft: '47px', marginBottom: '10px' }}>
                      <Skeleton variant="text" width="90%" height={18} sx={{ mb: 0.3 }} />
                      <Skeleton variant="text" width="75%" height={18} />
                    </div>
                    
                    {/* Meta Info Badges */}
                    <div style={{ marginLeft: '47px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <Skeleton variant="rectangular" width={150} height={28} sx={{ borderRadius: '4px' }} />
                      <Skeleton variant="rectangular" width={100} height={28} sx={{ borderRadius: '4px' }} />
                    </div>
                    
                    {/* Progress Bar */}
                    <div style={{ marginLeft: '47px', marginTop: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <Skeleton variant="text" width={60} height={16} />
                        <Skeleton variant="text" width={40} height={16} />
                      </div>
                      <Skeleton variant="rectangular" width="100%" height={10} sx={{ borderRadius: '5px' }} />
                    </div>
                  </div>
                  
                  {/* Action Button */}
                  <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: '6px', ml: 2 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !repo) {
    return (
      <div className="container" style={{ backgroundColor: 'white', minHeight: '100vh', borderRadius: '12px' }}>
        <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.5 }}>⚠️</div>
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '10px', color: '#2d3748' }}>
            {error || 'Code Repository Not Found'}
          </h2>
          <p style={{ color: '#718096', marginBottom: '20px', fontSize: '14px' }}>
            This code repository doesn't exist or you don't have access to it.
          </p>
          <button
            onClick={() => navigate('/my-code-repos')}
            className="btn btn-secondary"
          >
            ← Back to My Code Repos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ backgroundColor: 'white', minHeight: '100vh', borderRadius: '12px' }}>
      {/* Header Card */}
      <div className="card">
        <div style={{ marginBottom: '24px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/my-code-repos')}
            style={{ fontSize: '12px', marginBottom: '16px' }}
          >
            ← Back to My Code Repos
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
            <h2 style={{ color: '#667eea', fontSize: '22px', fontWeight: '700', margin: 0 }}>
              {repo.title}
            </h2>
            <span style={{
              padding: '4px 10px',
              backgroundColor: '#f7fafc',
              color: '#374151',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              fontFamily: 'monospace'
            }}>
              {repo.repoCode}
            </span>
            <span style={{
              padding: '4px 10px',
              backgroundColor: '#f7fafc',
              color: getLevelColor(repo.level),
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'capitalize',
              border: `1px solid ${getLevelColor(repo.level)}40`
            }}>
              {repo.level}
            </span>
          </div>
          {repo.description && (
            <p style={{ color: '#718096', fontSize: '13px', marginBottom: '12px' }}>
              {repo.description}
            </p>
          )}
          
          {/* Meta Info */}
          <div style={{
            display: 'flex',
            gap: '12px',
            fontSize: '12px',
            color: '#6b7280',
            flexWrap: 'wrap',
            marginTop: '12px'
          }}>
            {repo.category && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px',
                padding: '4px 10px',
                backgroundColor: '#f7fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontWeight: '500'
              }}>
                <span>📂</span>
                <span>{repo.category.name}</span>
              </div>
            )}

            {(repo.startDate || repo.endDate) && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px',
                padding: '4px 10px',
                backgroundColor: '#f7fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontWeight: '500'
              }}>
                <span>🗓️</span>
                <span>
                  {repo.startDate && formatDate(repo.startDate)}
                  {repo.startDate && repo.endDate && ' - '}
                  {repo.endDate && formatDate(repo.endDate)}
                </span>
              </div>
            )}

            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              padding: '4px 10px',
              backgroundColor: '#f7fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontWeight: '500'
            }}>
              <span>📝</span>
              <span>
                <strong>{repo.assignments?.length || 0}</strong> Assignments
              </span>
            </div>
          </div>

          {/* Tags */}
          {repo.tags && repo.tags.length > 0 && (
            <div style={{ marginTop: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {repo.tags.map(tag => (
                <span
                  key={tag._id}
                  style={{
                    padding: '4px 10px',
                    backgroundColor: '#eef2ff',
                    color: '#667eea',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    border: '1px solid #c7d2fe'
                  }}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Overall Progress */}
      {repo.assignments && repo.assignments.length > 0 && (
        <div className="card" style={{
          padding: '20px',
          marginBottom: '20px',
          backgroundColor: 'white',
          border: '1.5px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748', margin: 0 }}>
              📊 Overall Progress
            </h3>
            <span style={{
              fontSize: '18px',
              fontWeight: '700',
              color: calculateOverallProgress() === 100 ? '#10b981' : '#667eea'
            }}>
              {calculateOverallProgress()}%
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '12px',
            backgroundColor: '#e2e8f0',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${calculateOverallProgress()}%`,
              height: '100%',
              backgroundColor: calculateOverallProgress() === 100 ? '#10b981' : '#667eea',
              transition: 'width 0.3s ease',
              borderRadius: '6px'
            }} />
          </div>
          <div style={{
            marginTop: '10px',
            fontSize: '13px',
            color: '#718096',
            display: 'flex',
            gap: '16px'
          }}>
            <span>
              <strong style={{ color: '#2d3748' }}>
                {repo.assignments.filter(a => submissions[a.assignmentCode]?.isCompleted).length}
              </strong> / {repo.assignments.length} Completed
            </span>
            <span>•</span>
            <span>
              <strong style={{ color: '#2d3748' }}>
                {repo.assignments.filter(a => submissions[a.assignmentCode] && !submissions[a.assignmentCode].isCompleted).length}
              </strong> In Progress
            </span>
          </div>
        </div>
      )}

      {/* Assignments List */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '600',
          marginBottom: '20px',
          color: '#1f2937'
        }}>
          Assignments ({repo.assignments?.length || 0})
        </h2>

        {!repo.assignments || repo.assignments.length === 0 ? (
          <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>📝</div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px' }}>
              No Assignments Yet
            </h3>
            <p style={{ color: '#6b7280' }}>
              This repository doesn't have any assignments yet. Check back later!
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '15px' }}>
            {repo.assignments.map((assignment, index) => (
              <div
                key={assignment._id}
                className="card"
                style={{
                  padding: '20px',
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                      <span style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#6b7280',
                        minWidth: '35px'
                      }}>
                        #{index + 1}
                      </span>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        margin: 0,
                        color: '#1f2937'
                      }}>
                        {assignment.title}
                      </h3>
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: 'white',
                        color: getLevelColor(assignment.level),
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'capitalize',
                        border: `1px solid ${getLevelColor(assignment.level)}40`
                      }}>
                        {assignment.level}
                      </span>
                      {submissions[assignment.assignmentCode] && (
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: 'white',
                          color: getScoreColor(submissions[assignment.assignmentCode].totalScore || 0),
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          border: `1px solid ${getScoreColor(submissions[assignment.assignmentCode].totalScore || 0)}40`
                        }}>
                          {submissions[assignment.assignmentCode].totalScore || 0}%
                        </span>
                      )}
                    </div>

                    {assignment.description && (
                      <p style={{
                        color: '#6b7280',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        marginLeft: '47px',
                        marginBottom: '10px'
                      }}>
                        {assignment.description.length > 150
                          ? assignment.description.substring(0, 150) + '...'
                          : assignment.description}
                      </p>
                    )}

                    <div style={{
                      marginLeft: '47px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      flexWrap: 'wrap'
                    }}>
                      {assignment.estimatedTime && (
                        <div style={{
                          padding: '4px 10px',
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          fontSize: '12px',
                          color: '#6b7280',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontWeight: '500'
                        }}>
                          <span>⏱️</span>
                          <span>Est. Time: {assignment.estimatedTime} min</span>
                        </div>
                      )}
                      
                      {/* Status Badge */}
                      <div style={{
                        padding: '4px 10px',
                        backgroundColor: 'white',
                        color: getAssignmentStatus(assignment).color,
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        border: `1px solid ${getAssignmentStatus(assignment).color}40`
                      }}>
                        {getAssignmentStatus(assignment).label}
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    {submissions[assignment.assignmentCode] && (
                      <div style={{ marginLeft: '47px', marginTop: '12px' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '6px'
                        }}>
                          <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                            Progress
                          </span>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: calculateProgress(assignment) === 100 ? '#10b981' : '#667eea'
                          }}>
                            {calculateProgress(assignment)}%
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: '#e2e8f0',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${calculateProgress(assignment)}%`,
                            height: '100%',
                            backgroundColor: calculateProgress(assignment) === 100 ? '#10b981' : '#667eea',
                            transition: 'width 0.3s ease',
                            borderRadius: '4px'
                          }} />
                        </div>
                        <div style={{
                          marginTop: '6px',
                          fontSize: '11px',
                          color: '#9ca3af'
                        }}>
                          {submissions[assignment.assignmentCode].stepSubmissions?.filter(ss => ss.isComplete).length || 0} / {getTotalSteps(assignment.assignmentCode)} steps completed
                          {submissions[assignment.assignmentCode].stepSubmissions?.filter(ss => ss.isComplete).length === getTotalSteps(assignment.assignmentCode) && 
                           !submissions[assignment.assignmentCode].isCompleted && (
                            <span style={{ marginLeft: '6px', color: '#f59e0b', fontStyle: 'italic' }}>
                              • Quizzes pending
                            </span>
                          )}
                        </div>
                        
                        {/* Time Spent - Show only for completed assignments */}
                        {submissions[assignment.assignmentCode].isCompleted && submissions[assignment.assignmentCode].timeSpent > 0 && (
                          <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', color: '#718096' }}>
                              ⏱️ Time Spent:
                            </span>
                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#667eea' }}>
                              {formatTimeSpent(submissions[assignment.assignmentCode].timeSpent)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleSolveAssignment(assignment._id, assignment.assignmentCode)}
                    className="btn btn-primary"
                    style={{
                      marginLeft: '20px',
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: '600',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {submissions[assignment.assignmentCode]?.isCompleted
                      ? '✓ Review'
                      : submissions[assignment.assignmentCode]
                      ? 'Continue →'
                      : 'Start →'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeRepoDetail;
